import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { empty } from '../helpers';
import { IndividualAffiliationService } from '../individual-affiliation.service';
import { requiresAddress } from '../wizard-guard.service';
import { WizardStep } from '../wizard-step';
import { WizardService } from './wizard.service';

export interface IWizardNavigationService {
  next(source: WizardStep, current: ActivatedRoute | null): void;
  skip(source: WizardStep, current: ActivatedRoute | null): void;
}

@Injectable({
  providedIn: 'root'
})
export class WizardNavigationService implements IWizardNavigationService {

  constructor(
    private wizard: WizardService,
    private affiliation: IndividualAffiliationService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }

  public next(source: WizardStep, current = null): void {
    if (source.ready()) {
      switch (source.Name) {
        case 'EmailComponent':
          this.router.navigate(['/signup/contact', { email: this.wizard.Individual.eml_address }], { queryParams: { email: null }, queryParamsHandling: 'merge' });
          break;
        case 'LoginComponent':
          if (this.wizard.HasReferral) {
            this.wizard.GetReferral(this.wizard.Referral).subscribe(result => this.wizard.setReferral(result));
          }

          if (empty(this.wizard.Individual.ind_badge_name)) {
            this.router.navigate(['/signup/contact'], { queryParamsHandling: 'preserve' });
            return;
          } else if (empty(this.wizard.Individual.cxa_key) || (empty(this.wizard.Individual.adr_line1) && requiresAddress(this.wizard))) {
            this.router.navigate(['/signup/address'], { queryParamsHandling: 'preserve' });
            return;
          } else if (this.wizard.isAffinity) {
            this.router.navigate(['/signup/AffinityPortal', this.wizard.Affinity, 'select']);
            return;
          } else if (this.wizard.HasProspectType) {
            this.router.navigate(['/signup/demographics'], { queryParamsHandling: 'preserve' });
            return;
          } else if (this.route.snapshot.paramMap.has('destination')) {
            const destination = this.route.snapshot.paramMap.get('destination');
            this.router.navigate([destination]);
            return;
          } else if (this.route.snapshot.queryParamMap.has('destination')) {
            const destination = this.route.snapshot.queryParamMap.get('destination');
            if (destination.startsWith('/')) {
              window.location.href = destination;
            } else {
              window.location.href = '/' + destination;
            }
            return;
          }

          // No break, we want this to fall down the stack
        case 'AddressComponent':
          if(this.wizard.HasProspectType) {
            this.wizard.SyncIndividual().subscribe(() => {
              this.router.navigate(['/signup/demographics']);
            });
          }
          else if (this.wizard.isAffinity) {
            const query = {};

            if (this.wizard.isTBS) {
              query['massagetherapy'] = 'tbs';
            }

            this.wizard.SyncIndividual().subscribe(() => {
              this.router.navigate(['/signup/AffinityPortal', this.wizard.Affinity, 'select'], { queryParams: query });
            });
          } else if (this.wizard.HasReferral) {
            this.wizard.SyncIndividual().subscribe(() => {
              this.router.navigate(['/signup/referral', this.wizard.Referral]);
            });
          } else if (this.route.snapshot.paramMap.has('destination')) {
            const destination = this.route.snapshot.paramMap.get('destination');
            this.router.navigate([destination]);
          } else if (this.route.snapshot.queryParamMap.has('destination')) {
            const destination = this.route.snapshot.queryParamMap.get('destination');
            if (destination.startsWith('/')) {
              window.location.href = destination;
            } else {
              window.location.href = '/' + destination;
            }
          } else {
            combineLatest(this.wizard.SyncIndividual(), this.affiliation.GetAffiliation().pipe(map(affiliations => affiliations.filter(ixo => ixo.ixo_end_date === null)))).subscribe(([, affiliations]) => {
              if (this.wizard.HasDestination) {
                this.document.location.href = this.wizard.Destination.href;
              } else {
                if (affiliations.length) {
                  this.router.navigate(['/signup/affiliations'], { queryParamsHandling: 'preserve' });
                } else {
                  this.router.navigate(['/signup/selection'], { queryParamsHandling: 'preserve' });
                }
              }
            });
          }
          break;
        case 'ProspectDemographicsComponent':
          if (this.wizard.HasDestination) {
            this.document.location.href = this.wizard.Destination.href;
          } else {
            this.router.navigate(['/members']);
          }
          break;
        case 'ReferralComponent':
          combineLatest(this.wizard.SyncIndividual(), this.affiliation.GetAffiliation().pipe(map(affiliations => affiliations.filter(ixo => ixo.ixo_end_date === null)))).subscribe(([, affiliations]) => {
            if (affiliations.length) {
              this.router.navigate(['/signup/affiliations'], { queryParamsHandling: 'preserve' });
            } else {
              this.router.navigate(['/signup/selection'], { queryParamsHandling: 'preserve' });
            }
          });
          break;
        case 'IndividualAffiliationsComponent':
          this.wizard.SyncIndividual().subscribe(() => {
            this.router.navigate(['/signup/selection'], { queryParamsHandling: 'preserve' });
          });
          break;
        case 'ContactInfoComponent':
          if (this.wizard.HasReferral) {
            this.wizard.GetReferral(this.wizard.Referral).subscribe(result => this.wizard.setReferral(result));
          }

          if (empty(this.wizard.Individual.cxa_key) || empty(this.wizard.Individual.adr_line1)) {
            this.router.navigate(['/signup/address'], { queryParamsHandling: 'preserve' });
          } else if (this.route.snapshot.paramMap.has('destination')) {
            const destination = this.route.snapshot.paramMap.get('destination');
            this.router.navigate([destination]);
          } else if (this.route.snapshot.queryParamMap.has('destination')) {
            const destination = this.route.snapshot.queryParamMap.get('destination');
            if (destination.startsWith('/')) {
              window.location.href = destination;
            } else {
              window.location.href = '/' + destination;
            }
          } else {
            this.router.navigate(['/signup/address'], { queryParamsHandling: 'preserve' });
          }
          break;
      }
    }
  }

  public skip(source: WizardStep, current = null): void {
    if (source.ready()) {
      switch (source.Name) {
        case 'ContactInfoComponent':
          this.router.navigate(['/signup/address']);
        break;
        case 'ProspectDemographicsComponent':
          this.next(source);
        break;
      }
    }
  }
}
